import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import { fetchMember } from '@/features/members/api';
import { MemberRelatedNav } from '@/features/members/MemberRelatedNav';
import { MemberPresidentSurveyTab } from '@/features/presidentSurveys/MemberPresidentSurveyTab';
import { ApiError } from '@/lib/api/http';

/**
 * 社長アンケートの入力ページ（D-315）。
 *
 * 入力の仕組み自体は D-146 の時点で `MemberPresidentSurveyTab` にあったが、
 * 会員詳細のセクションの中だけに置かれていたため、プロフィール編集の画面から辿れず
 * 「CSVでしか入れられない」と受け取られていた（実機フィードバック）。
 * 会員の関連ページ（プロフィール編集・連携設定編集など）と同じ並びに独立したページを置く。
 */
export function MemberPresidentSurveyPage(): ReactElement {
  const { memberId = '' } = useParams();
  const query = useQuery({
    queryKey: ['member', memberId],
    queryFn: () => fetchMember(memberId),
    enabled: Boolean(memberId),
  });

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    return forbidden ? (
      <ForbiddenState />
    ) : (
      <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
    );
  }

  const member = query.data;

  return (
    <>
      <PageHeader
        title={`${member.name} さんの社長アンケート`}
        description="社長への聞き取り結果を、事務局が代理で入力します。受講生や社長がフォームに答える仕組みではありません。人数が多いときは、左メニュー「会員管理」→「社長アンケートCSV取込」でまとめて取り込めます。"
      />
      <MemberRelatedNav memberId={member.id} current="president-survey" />
      <MetaBox title="社長アンケート">
        <MemberPresidentSurveyTab memberId={member.id} />
      </MetaBox>
      <Link to={`/members/${member.id}`} className="mt-4 inline-block text-wpaccent hover:underline">
        ← 会員詳細に戻る
      </Link>
    </>
  );
}
